import type { MenuProps } from 'antd';
import { Space } from 'antd';
import { addMilliseconds, format, startOfToday } from 'date-fns';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { VIDEO_TYPE, VIDEO_360_TYPE, MEDIA_TYPE, PROCESSING_STATUS, STATUS_FAILED } from 'constants/MediaConstants';
import type { MayBe } from 'modules/global/typing/common/common';
import Button from 'ui/base/Button';
import Dropdown from 'ui/base/Dropdown';
import { useModal } from 'ui/base/Modal';
import Tooltip from 'ui/base/Tooltip';
import { getExtension, getName } from 'utils/fileName';
import { camelCase } from 'utils/global';

import type { MediaGalleryActions, MediaListItem } from '../types';

import useActionsMenuItems from './hooks/useActionsMenuItems';
import useVideoDuration from './hooks/useVideoDuration';
import { List, MediaActionsFooter, MediaTypeIcon, Time, FilenameWrapper } from './UI';
import { PROCESSING_TOOLTIP_TRANSLATIONS, PROCESSING_TRANSLATIONS } from './utils';

type ActionsProps = {
  media: MediaListItem;
} & MediaGalleryActions;

const Actions: React.FC<ActionsProps> = props => {
  const intl = useIntl();
  const modal = useModal();

  const { media, onClipping, onDownload, onRetry, onDelete } = props;
  const { filename, id: mediaId, processingStatus, path, thumbnailPath } = media.data;

  const { items, hasVisibleItems } = useActionsMenuItems({ ...props, media });
  const duration = useVideoDuration(media.data);

  const hasContent = Boolean(path || thumbnailPath);
  const hasFailed = media.status === STATUS_FAILED;

  const hasFilename = Boolean(filename);
  const is360 = media.data.type === VIDEO_360_TYPE;
  const isPendingProcess = !hasContent && !hasFailed;
  const isDocument = media.data.type === MEDIA_TYPE.DOCUMENT;
  const isProcessing = processingStatus && processingStatus !== PROCESSING_STATUS.PROCESSED;
  const showTimer = !isProcessing && hasContent && media.data.type === VIDEO_TYPE && duration >= 0;

  const { title: processingTitle, tooltip: processingTooltip } = React.useMemo(
    () => ({
      title: processingStatus ? PROCESSING_TRANSLATIONS[processingStatus] : undefined,
      tooltip: processingStatus ? PROCESSING_TOOLTIP_TRANSLATIONS[processingStatus] : undefined
    }),
    [processingStatus]
  );

  const handleClipping = React.useCallback(() => {
    onClipping?.(mediaId);
  }, [mediaId, onClipping]);

  const confirmClippingOnClick = React.useCallback(
    () =>
      modal.confirm({
        title: intl.formatMessage({
          id: 'clipping.clip',
          defaultMessage: 'Clip'
        }),
        content: intl.formatMessage({
          id: 'clipping.confirmation',
          defaultMessage: 'Are you sure you want to clip this picture?'
        }),
        okText: intl.formatMessage({
          id: 'clipping.buttonConfirmation',
          defaultMessage: 'Yes, clip'
        }),
        onOk: handleClipping,
        okButtonProps: {
          type: 'primary'
        }
      }),
    [handleClipping, intl, modal]
  );

  const actionCollection: Record<string, MayBe<(mediaListItem: MediaListItem) => void>> = React.useMemo(
    () => ({
      onClipping: confirmClippingOnClick,
      onDownload,
      onRetry,
      onDelete
    }),
    [confirmClippingOnClick, onDownload, onRetry, onDelete]
  );

  const handleActionOnClick = React.useCallback(
    ({ key: action }: Parameters<NonNullable<MenuProps['onClick']>>[0]) => {
      actionCollection[camelCase(`on_${action}`)]?.(media);
    },
    [actionCollection, media]
  );

  return (
    <MediaActionsFooter>
      <List>
        {isProcessing && processingTitle && processingTooltip && (
          <Space>
            <FormattedMessage {...processingTitle} />
            <Tooltip title={<FormattedMessage {...processingTooltip} />}>
              <i className="fb-icon fb-icon--info text-secondary cursor-pointer" />
            </Tooltip>
          </Space>
        )}
        {!isProcessing && (
          <React.Fragment>
            {showTimer && <Time>{format(addMilliseconds(startOfToday(), duration * 1000), 'HH:mm:ss')}</Time>}
            {isDocument && hasFilename && filename && (
              <Tooltip title={filename} placement="top">
                <FilenameWrapper>
                  <span>{getName(filename)}</span>
                  <span>.{getExtension(filename)}</span>
                </FilenameWrapper>
              </Tooltip>
            )}
          </React.Fragment>
        )}
        {is360 && (
          <li>
            <MediaTypeIcon className="fb-icon--360-degree" />
          </li>
        )}
      </List>
      {!isPendingProcess && hasVisibleItems && (
        <Dropdown menu={{ items, onClick: handleActionOnClick }}>
          <Button
            size="small"
            data-cy={`media-actions-${media.data.id}`}
            icon={<span className="fb-icon--elipsis-v" />}
          />
        </Dropdown>
      )}
    </MediaActionsFooter>
  );
};

export default Actions;
